/**
 * Responsive behavior for the ACF "Charts" block (a comparison DATA TABLE —
 * `.desktop-table` / `.mobile-table`), not the native bar-chart block.
 *
 * Iframe-aware, mirroring assets/js/accordion.js: the same module runs on the
 * front end (main bundle) and in the WP 7.0 block-editor canvas iframe
 * (enqueued via `enqueue_block_assets`). All DOM/viewport access is scoped to a
 * passed-in document + window so it works against the canvas document too.
 */
function updateMobileLabels( doc, win ) {
	const isMobile = win.innerWidth < 768;
	const headingCells = doc.querySelectorAll(
		'.mobile-table .is-left-column .chart-cell'
	);

	doc.querySelectorAll(
		'.mobile-table .chart-column:not(.is-left-column)'
	).forEach( ( column ) => {
		column.querySelectorAll( '.chart-cell' ).forEach( ( cell, index ) => {
			const mobileLabelContainer = cell.querySelector( '.label' );

			if ( mobileLabelContainer ) {
				if ( isMobile && headingCells[ index ] ) {
					if ( ! mobileLabelContainer.dataset.labeled ) {
						const labelSpan = doc.createElement( 'span' );
						mobileLabelContainer.classList.add(
							'flex-1',
							'pr-4',
							'min-w-1/3'
						);
						labelSpan.innerHTML = headingCells[ index ].innerHTML;
						mobileLabelContainer.appendChild( labelSpan );
						mobileLabelContainer.dataset.labeled = 'true';
					}
				} else {
					mobileLabelContainer.classList.remove(
						'flex-1',
						'pr-4',
						'min-w-1/3'
					);
					mobileLabelContainer.innerHTML = '';
					delete mobileLabelContainer.dataset.labeled;
				}
			}
		} );
	} );
}

function handleMobileTable( doc, win ) {
	const isMobile = win.innerWidth < 768;

	doc.querySelectorAll(
		'.mobile-table .chart-column:not(.is-left-column)'
	).forEach( ( column ) => {
		const cells = Array.from(
			column.querySelectorAll( '.chart-cell' )
		);
		let buttonContainer = column.querySelector( '.show-more-container' );
		let showMoreBtn = column.querySelector( '.show-more-btn' );

		if ( isMobile ) {
			if ( cells.length > 6 ) {
				cells.slice( 6 ).forEach( ( cell ) =>
					cell.classList.add( 'hidden' )
				);

				if ( ! buttonContainer ) {
					buttonContainer = doc.createElement( 'div' );
					buttonContainer.classList.add(
						'show-more-container',
						'flex',
						'justify-center',
						'items-center',
						'p-3'
					);

					showMoreBtn = doc.createElement( 'button' );
					showMoreBtn.textContent = 'Show More';
					showMoreBtn.classList.add(
						'show-more-btn',
						'button',
						'medium',
						'secondary-on-light'
					);

					showMoreBtn.addEventListener( 'click', () => {
						const isExpanded =
							showMoreBtn.textContent === 'Show Less';
						cells.slice( 6 ).forEach( ( cell ) =>
							cell.classList.toggle( 'hidden', isExpanded )
						);
						showMoreBtn.textContent = isExpanded
							? 'Show More'
							: 'Show Less';
					} );

					buttonContainer.appendChild( showMoreBtn );
					column.appendChild( buttonContainer );
				}
			}
		} else {
			cells.forEach( ( cell ) => cell.classList.remove( 'hidden' ) );
			if ( buttonContainer ) {
				buttonContainer.remove();
			}
		}
	} );
}

function updateTableAriaHidden( doc, win ) {
	const desktopTable = doc.querySelector( '.desktop-table' );
	const mobileTable = doc.querySelector( '.mobile-table' );
	const isMobile = win.innerWidth < 768;

	if ( desktopTable && mobileTable ) {
		desktopTable.setAttribute( 'aria-hidden', isMobile ? 'true' : 'false' );
		mobileTable.setAttribute( 'aria-hidden', isMobile ? 'false' : 'true' );
	}
}

/**
 * Idempotent init for a given root document. Binds resize handling once per
 * document (flagged on the root element) and runs the layout pass immediately.
 */
function initCharts( root = document ) {
	const doc = root;
	const win = doc.defaultView || window;

	const run = () => {
		updateMobileLabels( doc, win );
		handleMobileTable( doc, win );
		updateTableAriaHidden( doc, win );
	};

	run();

	if ( doc.documentElement.dataset.chartsResizeBound !== 'true' ) {
		doc.documentElement.dataset.chartsResizeBound = 'true';
		win.addEventListener( 'resize', run );
	}
}

// Reuse the bootstrapper exposed by accordion.js when present; otherwise define
// an identical local copy so this file stays self-contained.
function dequeBootstrapCanvasScript( canvasInit ) {
	if (
		typeof window !== 'undefined' &&
		typeof window.dequeBootstrapCanvasScript === 'function' &&
		window.dequeBootstrapCanvasScript !== dequeBootstrapCanvasScript
	) {
		return window.dequeBootstrapCanvasScript( canvasInit );
	}

	const runWhenReady = ( fn ) => {
		if ( document.readyState === 'loading' ) {
			document.addEventListener( 'DOMContentLoaded', fn );
		} else {
			fn();
		}
	};

	const inEditor =
		typeof window !== 'undefined' &&
		window.wp &&
		window.wp.data &&
		window.wp.domReady;

	if ( ! inEditor ) {
		runWhenReady( () => canvasInit( document ) );
		return;
	}

	window.wp.domReady( () => {
		const canvasDoc = () => {
			const iframe = document.querySelector(
				'iframe[name="editor-canvas"]'
			);
			return iframe && iframe.contentDocument
				? iframe.contentDocument
				: document;
		};

		let scheduled;
		const reinit = () => canvasInit( canvasDoc() );

		reinit();
		window.wp.data.subscribe( () => {
			clearTimeout( scheduled );
			scheduled = setTimeout( reinit, 300 );
		} );
	} );
}

dequeBootstrapCanvasScript( initCharts );
